'use client';

import React, { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface CalculatorInputs {
  currentRevenue: number;
  projectsPerYear: number;
  avgProjectValue: number;
}

interface CalculatorResults {
  potentialRevenue: number;
  revenueIncrease: number;
  additionalProjects: number;
  networkValue: number;
}

const BenefitsCalculatorSection = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [inputs, setInputs] = useState<CalculatorInputs>({
    currentRevenue: 150000,
    projectsPerYear: 6,
    avgProjectValue: 25000
  });
  const [results, setResults] = useState<CalculatorResults>({
    potentialRevenue: 0,
    revenueIncrease: 0,
    additionalProjects: 0,
    networkValue: 0
  });

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  useEffect(() => {
    if (!isHydrated) return;

    const revenueGrowthMultiplier = 2.4;
    const projectIncreaseMultiplier = 1.8;
    const avgProjectValueIncrease = 1.35;
    
    const potentialRevenue = inputs.currentRevenue * revenueGrowthMultiplier;
    const revenueIncrease = potentialRevenue - inputs.currentRevenue;
    const additionalProjects = Math.round(inputs.projectsPerYear * (projectIncreaseMultiplier - 1));
    const networkValue = inputs.avgProjectValue * avgProjectValueIncrease * additionalProjects;
    
    setResults({
      potentialRevenue,
      revenueIncrease,
      additionalProjects,
      networkValue
    });
  }, [inputs, isHydrated]);

  const handleInputChange = (field: keyof CalculatorInputs, value: string) => {
    const numValue = parseFloat(value) || 0;
    setInputs(prev => ({ ...prev, [field]: numValue }));
  };

  const formatCurrency = (value: number): string => {
    return `£${value.toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  };

  if (!isHydrated) {
    return (
      <section className="py-20 lg:py-32 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="bg-card rounded-xl shadow-card p-8 border border-border">
              <div className="animate-pulse space-y-4">
                <div className="h-8 bg-muted rounded w-3/4"></div>
                <div className="h-4 bg-muted rounded w-1/2"></div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-8">
                  <div className="h-24 bg-muted rounded"></div>
                  <div className="h-24 bg-muted rounded"></div>
                  <div className="h-24 bg-muted rounded"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-20 lg:py-32 bg-background">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-5xl font-bold font-headline text-primary mb-6">
              Partnership Benefits Calculator
            </h2>
            <p className="text-lg text-muted-foreground font-body">
              Estimate your potential revenue growth and network value as an AI Change Leaders partner.
            </p>
          </div>
          
          <div className="bg-card rounded-xl shadow-card p-8 border border-border">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <div>
                <label className="block text-sm font-semibold text-foreground font-body mb-2">
                  Current Annual Revenue (£)
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                    £
                  </span>
                  <input
                    type="number"
                    value={inputs.currentRevenue}
                    onChange={(e) => handleInputChange('currentRevenue', e.target.value)}
                    className="w-full pl-8 pr-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-secondary bg-background text-foreground font-body"
                    min="0"
                    step="10000"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-semibold text-foreground font-body mb-2">
                  Projects Per Year
                </label>
                <input
                  type="number"
                  value={inputs.projectsPerYear}
                  onChange={(e) => handleInputChange('projectsPerYear', e.target.value)}
                  className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-secondary bg-background text-foreground font-body"
                  min="0"
                  step="1"
                />
              </div>
              
              <div>
                <label className="block text-sm font-semibold text-foreground font-body mb-2">
                  Avg Project Value (£)
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                    £
                  </span>
                  <input
                    type="number"
                    value={inputs.avgProjectValue}
                    onChange={(e) => handleInputChange('avgProjectValue', e.target.value)}
                    className="w-full pl-8 pr-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-secondary bg-background text-foreground font-body"
                    min="0"
                    step="5000"
                  />
                </div>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-gradient-to-br from-success/10 to-success/5 rounded-lg p-6 border border-success/20">
                <div className="flex items-center gap-2 mb-2">
                  <Icon name="TrendingUpIcon" size={20} variant="solid" className="text-success" />
                  <span className="text-sm font-semibold text-foreground font-body">
                    Potential Revenue
                  </span>
                </div>
                <div className="text-2xl font-bold font-headline text-success">
                  {formatCurrency(results.potentialRevenue)}
                </div>
                <div className="text-xs text-muted-foreground font-body mt-1">
                  Based on 2.4x growth
                </div>
              </div>
              
              <div className="bg-gradient-to-br from-accent/10 to-accent/5 rounded-lg p-6 border border-accent/20">
                <div className="flex items-center gap-2 mb-2">
                  <Icon name="ArrowTrendingUpIcon" size={20} variant="solid" className="text-accent" />
                  <span className="text-sm font-semibold text-foreground font-body">
                    Revenue Increase
                  </span>
                </div>
                <div className="text-2xl font-bold font-headline text-accent">
                  {formatCurrency(results.revenueIncrease)}
                </div>
                <div className="text-xs text-muted-foreground font-body mt-1">
                  Additional annual revenue
                </div>
              </div>
              
              <div className="bg-gradient-to-br from-secondary/10 to-secondary/5 rounded-lg p-6 border border-secondary/20">
                <div className="flex items-center gap-2 mb-2">
                  <Icon name="BriefcaseIcon" size={20} variant="solid" className="text-secondary" />
                  <span className="text-sm font-semibold text-foreground font-body">
                    Additional Projects
                  </span>
                </div>
                <div className="text-2xl font-bold font-headline text-secondary">
                  +{results.additionalProjects}
                </div>
                <div className="text-xs text-muted-foreground font-body mt-1">
                  New opportunities per year
                </div>
              </div>
              
              <div className="bg-gradient-to-br from-primary/10 to-primary/5 rounded-lg p-6 border border-primary/20">
                <div className="flex items-center gap-2 mb-2">
                  <Icon name="CurrencyPoundIcon" size={20} variant="solid" className="text-primary" />
                  <span className="text-sm font-semibold text-foreground font-body">
                    Network Value
                  </span>
                </div>
                <div className="text-2xl font-bold font-headline text-primary">
                  {formatCurrency(results.networkValue)}
                </div>
                <div className="text-xs text-muted-foreground font-body mt-1">
                  From new projects
                </div>
              </div>
            </div>
            
            <div className="mt-8 bg-primary/5 rounded-lg p-6 border border-primary/20">
              <div className="flex items-start gap-3">
                <Icon name="InformationCircleIcon" size={24} variant="solid" className="text-primary flex-shrink-0 mt-1" />
                <div>
                  <h4 className="text-sm font-bold font-headline text-primary mb-2">
                    Calculation Methodology
                  </h4>
                  <p className="text-sm text-foreground font-body">
                    Estimates based on average partner performance data from 2025. Results include 2.4x revenue growth multiplier, 1.8x project increase, and 35% average project value increase. Individual results may vary based on expertise, market conditions, and engagement level.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BenefitsCalculatorSection;